import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EventLoggerService } from '@services/event-logger-service';
import { ConnectionService } from '@services/connection-service';
import { BroadcastService } from '@services/broadcast-service';
import { ApiClientConstant } from '@cureskin/api-client/src/constant/api-client.constant';
import { AppConfig } from '../../app.config';
import { ExperimentService } from '@services/experiment/experiment.service';
import { Experiment } from '@cureskin/api-client/src/server-api';
import { RegimenService } from '@services/regimen-service';
import { getPlanInfoConfig, planInfoItemId } from '@shared/choose-plan-info/choose-plan-info.constants';
import { UserAllocatedDoctorService } from '@services/user-allocated-doctor-service/user-allocated-doctor.service';

@Component({
  selector: 'user-regimen-services',
  templateUrl: './user-regimen-services.html',
  standalone: false,
})
export class UserRegimenServicesComponent {
  regimen: any;
  user: any;
  loading: boolean = true;
  offerTimer: any = {};
  allocatedDoctor: any;
  byPassLocalFetch: boolean = false;
  products: any = [];
  experiments: Array<Experiment>;
  regimenValuePropositionV2Experiment: boolean = false;
  planInfoConfig = getPlanInfoConfig();
  doctorImageUrl: string = '';
  planInfoItemId = planInfoItemId;

  constructor(private eventLogger: EventLoggerService,
    public appConfig: AppConfig,
    private conn: ConnectionService,
    private router: Router,
    private broadcast: BroadcastService,
    private route: ActivatedRoute,
    private readonly experimentService: ExperimentService,
    private readonly regimenService: RegimenService,
    private readonly userAllocatedDoctorService: UserAllocatedDoctorService) {
    this.doctorImageUrl = this.userAllocatedDoctorService.DEFAULT_DOCTOR_IMAGE_THUMBNAIL;
  }

  async ngOnInit(): Promise<void> {
    const regimenClass = this.route.snapshot.queryParams.class;
    const { regimenId }: any = this.route.snapshot.queryParams;
    this.byPassLocalFetch = true;
    this.user = await this.conn.getActingUser();
    const regimens = await this.conn.fetchRegimens(regimenId, true, this.byPassLocalFetch);
    await this.setUserExperiments();
    this.regimen = regimens.find((each: any): boolean => each.class === regimenClass);
    this.regimen.totalMRP = this.regimen?.fixedPriceMRP;
    this.regimen.totalSP = this.regimen?.fixedPrice;
    this.regimen.discount = Math.floor(((this.regimen.totalMRP - this.regimen.totalSP) * 100) / this.regimen.totalMRP);

    if (this.user.get('allocatedDoctor')) {
      this.allocatedDoctor = await this.conn.findUserByObjectId(this.user.get('allocatedDoctor').id);
      this.doctorImageUrl = this.allocatedDoctor?.get('DoctorImageThumbnail') || this.userAllocatedDoctorService.DEFAULT_DOCTOR_IMAGE_THUMBNAIL;
    }

    const uniqueProducts = {};
    [...this.regimen.morning, ...this.regimen.night].forEach((item: any): void => {
      if (item.product.price) uniqueProducts[item.product.objectId] = item;
    });
    this.products = Object.values(uniqueProducts);

    this.loading = false;
    this.eventLogger.cleverTapEvent('pageOpen', JSON.stringify({ pageName: 'regimen-services', rebrandedRegimenProposition: this.shouldShowRebrandedRegimenProposition }), [
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.MOENGAGE,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ]);
  }

  buyRegimen(): void {
    this.eventLogger.trackInParse('BUY_NOW', ApiClientConstant.Event.Type.ORDER);
    this.eventLogger.trackEvent('BUY_NOW_CLICKED');
    this.eventLogger.trackInFirebaseAndBranch('BUY_NOW_CLICKED');
    if (this.regimen.class === this.appConfig.Shared.Regimen.Class.HAIR) {
      this.eventLogger.trackEvent('BUY_NOW_CLICKED_HAIR');
      this.eventLogger.trackInFirebaseAndBranch('BUY_NOW_CLICKED_HAIR');
    }
    const orderState = this.user?.get('orderState');
    this.router.navigate(['/user/checkout'],
      { queryParams: { type: 'REGIMEN', regimenId: this.regimen.regimenId, useCureSkinCash: String(this.experimentService.shouldUseCureSkinCash(orderState)) }, replaceUrl: true });
  }

  back(): void {
    this.broadcast.broadcast('NAVIGATION_BACK');
  }

  getItemImage(item: any, index: number): string {
    // First item uses regimen cover image
    if (item.id === planInfoItemId.PRODUCTS && this.regimen?.regimenCoverImage) {
      return this.regimen?.regimenCoverImage;
    }
    // Items with 'dedicated_doctor' or 'one_on_one_doctor' id use allocated doctor's image
    if (item.id === planInfoItemId.DEDICATED_DOCTOR || item.id === planInfoItemId.ONE_ON_ONE_DOCTOR) {
      return this.doctorImageUrl || '';
    }
    // Otherwise use the configured image URL
    return item.imageUrl;
  }

  async setUserExperiments(): Promise<void> {
    this.experiments = await this.conn.findUserActiveExperiments();
    this.experiments.forEach((experiment): void => {
      if (experiment.key === 'regimen_value_proposition_v2') {
        this.regimenValuePropositionV2Experiment = true;
      }
    });
  }

  get shouldShowRebrandedRegimenProposition(): boolean {
    return this.regimenService.shouldShowRebrandedRegimenPropositionUI(this.regimenValuePropositionV2Experiment, this.regimen?.class);
  }
}
